import { setLoadingRowTestId } from "@bcc/components/data-table/data-table.utils";
import { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import type { TourStep } from "./tour.types";
import { setTourTestId, testIdSelector } from "./tour.utils";

/** The step's page is showing when the pathname is its route or any alternate pattern (redirect targets) */
const isOnStepRoute = (step: TourStep, pathname: string): boolean =>
  pathname === step.route || (step.alternateRoutePatterns ?? []).some((pattern) => matchPath(pattern, pathname) !== null);

/** The shared LoadingIcon — present in the DOM only while the app is genuinely loading */
export const LOADING_INDICATOR_SELECTOR = testIdSelector("LoadingIcon-spinner");

/** The tour's own "Preparing this step…" card also renders a LoadingIcon; it must never count as the app loading */
const TOUR_LOADING_CARD_SELECTOR = testIdSelector(setTourTestId("loading-card"));

/** DataTable skeleton rows (e.g. the products table) — shown instead of a LoadingIcon while table data loads */
export const LOADING_ROW_SELECTOR = testIdSelector(setLoadingRowTestId(0));

const isSpinnerOutsideTourShowing = (): boolean =>
  Array.from(document.querySelectorAll(LOADING_INDICATOR_SELECTOR)).some((spinner) => spinner.closest(TOUR_LOADING_CARD_SELECTOR) === null);

/** True while any LoadingIcon outside the tour's own loading card, or a DataTable loading skeleton, is on screen */
export const isAppLoading = (): boolean => isSpinnerOutsideTourShowing() || document.querySelector(LOADING_ROW_SELECTOR) !== null;

/** Absolute ceiling on waiting for a target, so a never-settling spinner can't hang the tour */
const MAX_TOTAL_WAIT_MS = 30000;
/** After the tour navigates to a step's page, it is given this long to start loading before a missing `skipUnless` prerequisite skips the step */
const PREREQUISITE_GRACE_MS = 1500;
/** How often the anchored rect is re-measured outside of scroll/resize events */
const RECT_SWEEP_MS = 150;

/** Built as a literal because jsdom has no DOMRect constructor */
const buildRect = (left: number, top: number, right: number, bottom: number): DOMRect =>
  ({
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    x: left,
    y: top,
    toJSON: () => ({ left, top, right, bottom }),
  }) as DOMRect;

/**
 * Radix triggers (e.g. Tabs) activate on pointer/mouse down rather than click,
 * so prepare actions dispatch the full press sequence before the final click.
 */
const activateTrigger = (trigger: HTMLElement): void => {
  const eventInit = { bubbles: true, cancelable: true, button: 0 };
  const PointerEventCtor = typeof PointerEvent === "undefined" ? MouseEvent : PointerEvent;
  trigger.dispatchEvent(new PointerEventCtor("pointerdown", eventInit));
  trigger.dispatchEvent(new MouseEvent("mousedown", eventInit));
  trigger.dispatchEvent(new PointerEventCtor("pointerup", eventInit));
  trigger.dispatchEvent(new MouseEvent("mouseup", eventInit));
  trigger.click();
};

export interface UseTourTargetOptions {
  /** The step currently shown; undefined when the tour is not running */
  step: TourStep | undefined;
  /** Only anchor while the walkthrough is actually running */
  active: boolean;
  /** Called when the target never appears — the engine auto-advances so the tour can't hang */
  onTargetMissing: () => void;
  /** Called when an interactive step's `advanceWhen` selector appears after the step anchored */
  onAdvance?: () => void;
  pollIntervalMs?: number;
  timeoutMs?: number;
}

export interface UseTourTargetResult {
  /** Live viewport rect of the target, or null while navigating/polling */
  rect: DOMRect | null;
  element: Element | null;
}

/**
 * Anchors the running tour step to its DOM target: navigates to the step's
 * route when needed, polls for the target element, scrolls it into view,
 * then tracks its rect through scroll/resize/layout changes.
 */
export function useTourTarget({
  step,
  active,
  onTargetMissing,
  onAdvance,
  pollIntervalMs = 100,
  timeoutMs = 6000,
}: UseTourTargetOptions): UseTourTargetResult {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [element, setElement] = useState<Element | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const onTargetMissingRef = useRef(onTargetMissing);
  const onAdvanceRef = useRef(onAdvance);
  // Read through a ref so the rect tracker stays keyed on the element alone —
  // re-running it on step change would measure the old element for the new step
  const stepRef = useRef(step);
  // The step the tour itself navigated for: its page has only just mounted and may not have begun fetching
  const navigatedStepIdRef = useRef<string>();

  useEffect(() => {
    onTargetMissingRef.current = onTargetMissing;
    onAdvanceRef.current = onAdvance;
  }, [onTargetMissing, onAdvance]);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    if (!active || !step) return;
    if (isOnStepRoute(step, pathname)) return;
    navigatedStepIdRef.current = step.id;
    navigate(step.route);
  }, [active, step, pathname, navigate]);

  // Reset the anchor in the same render the step (or page, or tour state) changes,
  // so the new step is never drawn at the previous step's position for a frame
  const anchorKey = `${active}|${step?.id ?? ""}|${pathname}`;
  const [anchoredKey, setAnchoredKey] = useState(anchorKey);
  if (anchoredKey !== anchorKey) {
    setAnchoredKey(anchorKey);
    setElement(null);
    setRect(null);
  }

  // Polls whenever no element is anchored — including after the anchored element
  // is removed from the DOM (e.g. a spotlighted dialog closed on outside-press),
  // so prepare actions can reopen it and the step re-anchors instead of hanging.
  useEffect(() => {
    if (!active || !step || element !== null || !isOnStepRoute(step, pathname)) return;
    let waitedMs = 0;
    const stepTimeoutMs = step.targetTimeoutMs ?? timeoutMs;
    const findTarget = (): boolean => {
      const found = document.querySelector(step.target);
      if (!found) return false;
      found.scrollIntoView?.({ block: "center" });
      setElement(found);
      return true;
    };
    const runPrepare = (): void => {
      for (const action of step.prepare ?? []) {
        const trigger = document.querySelector(action.clickTarget);
        if (trigger instanceof HTMLElement) activateTrigger(trigger);
      }
    };
    // The page has settled but the element this step depends on is absent
    // (e.g. no batch selected) — nothing will make the target appear, so skip now
    let totalElapsedMs = 0;
    const graceMs = navigatedStepIdRef.current === step.id ? PREREQUISITE_GRACE_MS : 0;
    const isPrerequisiteMissing = (): boolean =>
      step.skipUnless !== undefined && totalElapsedMs >= graceMs && !isAppLoading() && !document.querySelector(step.skipUnless);
    // Prepare runs before the first lookup so a step can close a leftover
    // dialog even when its own target is already visible behind it.
    runPrepare();
    if (findTarget()) return;
    if (isPrerequisiteMissing()) {
      onTargetMissingRef.current();
      return;
    }
    const interval = setInterval(() => {
      if (findTarget()) {
        clearInterval(interval);
        return;
      }
      runPrepare();
      if (isPrerequisiteMissing()) {
        clearInterval(interval);
        onTargetMissingRef.current();
        return;
      }
      // The timeout pauses while the app shows a loading spinner so slow
      // pages don't skip steps; the hard cap still ends the wait eventually.
      // Interactive steps wait for the user instead and never time out.
      if (!isAppLoading()) waitedMs += pollIntervalMs;
      totalElapsedMs += pollIntervalMs;
      if (!step.interactive && (waitedMs >= stepTimeoutMs || totalElapsedMs >= MAX_TOTAL_WAIT_MS)) {
        clearInterval(interval);
        onTargetMissingRef.current();
      }
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [active, step, pathname, element, pollIntervalMs, timeoutMs]);

  // Edge-triggered: arms once the target is anchored and the selector is still
  // absent, so stepping Back onto a step whose condition already holds stays put.
  useEffect(() => {
    const selector = step?.advanceWhen;
    if (!active || !selector || element === null || document.querySelector(selector) !== null) return;
    const interval = setInterval(() => {
      if (document.querySelector(selector) === null) return;
      clearInterval(interval);
      onAdvanceRef.current?.();
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [active, step, element, pollIntervalMs]);

  useEffect(() => {
    if (!element) return;
    // Steps highlighting controls in separate DOM subtrees grow the rect to
    // cover every extendTargets match; extras are re-measured on every rect
    // update (scroll/resize/500ms sweep), so their movements are tracked too.
    const unionWithExtras = (base: DOMRect): DOMRect => {
      let { top, left, right, bottom } = base;
      let extended = false;
      for (const selector of stepRef.current?.extendTargets ?? []) {
        const extra = document.querySelector(selector);
        if (!extra) continue;
        const extraRect = extra.getBoundingClientRect();
        // A closed or hidden extra has no box and must not stretch the spotlight to the corner
        if (extraRect.width === 0 && extraRect.height === 0) continue;
        top = Math.min(top, extraRect.top);
        left = Math.min(left, extraRect.left);
        right = Math.max(right, extraRect.right);
        bottom = Math.max(bottom, extraRect.bottom);
        extended = true;
      }
      return extended ? buildRect(left, top, right, bottom) : base;
    };
    const updateRect = () => {
      if (!element.isConnected) {
        setElement(null);
        setRect(null);
        return;
      }
      const next = unionWithExtras(element.getBoundingClientRect());
      setRect((prev) =>
        prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height ? prev : next
      );
    };
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateRect);
    resizeObserver?.observe(element);
    // Detached elements and newly opened extendTargets (e.g. a dropdown list) fire
    // no scroll/resize/observer events — sweep for them
    const connectivityInterval = setInterval(updateRect, RECT_SWEEP_MS);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      resizeObserver?.disconnect();
      clearInterval(connectivityInterval);
    };
  }, [element]);

  return { rect, element };
}
